import { openDB, type IDBPDatabase } from "idb";
import {
  GLOBAL_DB_NAME,
  GLOBAL_DB_VER,
  LEGACY_DB_NAME,
  LEGACY_DB_VER,
  META_IDENTITY_KEY,
  META_SYNC_SEQ_KEY,
  USER_DB_PREFIX,
  USER_DB_VER,
  type IdentityRow,
  type StyxGlobalSchema,
  type StyxLegacySchema,
  type StyxUserSchema,
  type SyncCursorRow,
} from "./types";

function createOutboxStore(database: IDBPDatabase<StyxUserSchema>): void {
  if (!database.objectStoreNames.contains("outbox")) {
    const o = database.createObjectStore("outbox", { keyPath: "id" });
    o.createIndex("by-peer-sort", ["peerId", "created_at", "id"], { unique: false });
  }
}

let globalDbPromise: Promise<IDBPDatabase<StyxGlobalSchema>> | null = null;
const userDbPromises = new Map<string, Promise<IDBPDatabase<StyxUserSchema>>>();

let activeUserId: string | null = null;
let legacyMigrationDone = false;

export function getActiveUserId(): string | null {
  return activeUserId;
}

export function setActiveUserId(userId: string | null): void {
  activeUserId = userId;
}

function userDbName(userId: string): string {
  return `${USER_DB_PREFIX}${userId}`;
}

async function getGlobalDb(): Promise<IDBPDatabase<StyxGlobalSchema>> {
  if (!globalDbPromise) {
    globalDbPromise = openDB<StyxGlobalSchema>(GLOBAL_DB_NAME, GLOBAL_DB_VER, {
      upgrade(database) {
        if (!database.objectStoreNames.contains("meta")) {
          database.createObjectStore("meta");
        }
      },
    });
  }
  return globalDbPromise;
}

function createCacheStores(database: IDBPDatabase<StyxUserSchema>): void {
  if (!database.objectStoreNames.contains("link_preview_cache")) {
    database.createObjectStore("link_preview_cache", { keyPath: "id" });
  }
  if (!database.objectStoreNames.contains("image_atr_cache")) {
    const img = database.createObjectStore("image_atr_cache", { keyPath: "blobId" });
    img.createIndex("by-lru", "lastAccessAt", { unique: false });
  }
}

function createUserStores(database: IDBPDatabase<StyxUserSchema>): void {
  if (!database.objectStoreNames.contains("meta")) {
    database.createObjectStore("meta");
  }
  if (!database.objectStoreNames.contains("contacts")) {
    const c = database.createObjectStore("contacts", { keyPath: "user_id" });
    c.createIndex("by-username", "username", { unique: false });
  }
  if (!database.objectStoreNames.contains("messages")) {
    const m = database.createObjectStore("messages", { keyPath: "id" });
    m.createIndex("by-peer", "peerId", { unique: false });
    m.createIndex("by-peer-sort", ["peerId", "created_at", "id"], { unique: false });
  }
  createCacheStores(database);
}

function upgradeMessagesStoreV2(transaction: IDBTransaction): void {
  const store = transaction.objectStore("messages");
  if (!store.indexNames.contains("by-peer-sort")) {
    store.createIndex("by-peer-sort", ["peerId", "created_at", "id"], { unique: false });
  }
}

async function openUserDb(userId: string): Promise<IDBPDatabase<StyxUserSchema>> {
  let p = userDbPromises.get(userId);
  if (!p) {
    p = openDB<StyxUserSchema>(userDbName(userId), USER_DB_VER, {
      upgrade(database, oldVersion, _newVersion, transaction) {
        createUserStores(database);
        if (oldVersion < 2 && database.objectStoreNames.contains("messages")) {
          upgradeMessagesStoreV2(transaction as unknown as IDBTransaction);
        }
        if (oldVersion < 3) {
          createCacheStores(database);
        }
        if (oldVersion < 4) {
          createOutboxStore(database);
        }
      },
    });
    userDbPromises.set(userId, p);
  }
  return p;
}

function deleteDbByName(name: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.deleteDatabase(name);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error ?? new Error("deleteDatabase failed"));
    req.onblocked = () => resolve();
  });
}

async function migrateLegacyStyxV1IfNeeded(): Promise<void> {
  if (legacyMigrationDone) return;
  legacyMigrationDone = true;

  let hasLegacyDb = false;
  if (typeof indexedDB.databases === "function") {
    try {
      const dbs = await indexedDB.databases();
      hasLegacyDb = dbs.some((d) => d.name === LEGACY_DB_NAME);
    } catch {
      hasLegacyDb = true;
    }
  } else {
    hasLegacyDb = true;
  }

  if (!hasLegacyDb) return;

  let legacy: IDBPDatabase<StyxLegacySchema> | null = null;
  try {
    legacy = await openDB<StyxLegacySchema>(LEGACY_DB_NAME, LEGACY_DB_VER, {
      upgrade(database) {
        if (!database.objectStoreNames.contains("meta")) {
          database.createObjectStore("meta");
        }
        if (!database.objectStoreNames.contains("contacts")) {
          const c = database.createObjectStore("contacts", { keyPath: "user_id" });
          c.createIndex("by-username", "username", { unique: false });
        }
        if (!database.objectStoreNames.contains("messages")) {
          const m = database.createObjectStore("messages", { keyPath: "id" });
          m.createIndex("by-peer", "peerId", { unique: false });
        }
      },
    });
  } catch {
    return;
  }

  if (!legacy.objectStoreNames.contains("meta")) {
    legacy.close();
    await deleteDbByName(LEGACY_DB_NAME);
    return;
  }

  const rawIdentity = await legacy.get("meta", META_IDENTITY_KEY);
  const legacyIdentity =
    rawIdentity &&
    typeof rawIdentity === "object" &&
    "email" in rawIdentity &&
    typeof (rawIdentity as IdentityRow).email === "string"
      ? (rawIdentity as IdentityRow)
      : undefined;

  const rawSync = await legacy.get("meta", META_SYNC_SEQ_KEY);
  const legacySync =
    rawSync &&
    typeof rawSync === "object" &&
    "value" in rawSync &&
    typeof (rawSync as SyncCursorRow).value === "number"
      ? (rawSync as SyncCursorRow)
      : undefined;

  const globalDb = await getGlobalDb();
  const globalIdentity = await globalDb.get("meta", META_IDENTITY_KEY);

  if (!legacyIdentity) {
    legacy.close();
    await deleteDbByName(LEGACY_DB_NAME);
    setActiveUserId(null);
    return;
  }

  const uid = legacyIdentity.userId;
  setActiveUserId(uid);
  const userDb = await openUserDb(uid);

  if (!globalIdentity) {
    await globalDb.put("meta", legacyIdentity, META_IDENTITY_KEY);
  }

  if (legacy.objectStoreNames.contains("contacts")) {
    const contacts = await legacy.getAll("contacts");
    const tx = userDb.transaction("contacts", "readwrite");
    for (const c of contacts) {
      await tx.store.put(c);
    }
    await tx.done;
  }

  if (legacy.objectStoreNames.contains("messages")) {
    const messages = await legacy.getAll("messages");
    const tx = userDb.transaction("messages", "readwrite");
    for (const m of messages) {
      await tx.store.put(m);
    }
    await tx.done;
  }

  if (legacySync) {
    await userDb.put("meta", legacySync, META_SYNC_SEQ_KEY);
  }

  legacy.close();
  await deleteDbByName(LEGACY_DB_NAME);
  setActiveUserId(null);
}

export async function getDb(): Promise<IDBPDatabase<StyxUserSchema>> {
  if (!activeUserId) {
    throw new Error("No active user — call setActiveUserId(userId) before accessing contacts/messages");
  }
  return openUserDb(activeUserId);
}

export async function loadIdentity(): Promise<IdentityRow | undefined> {
  await migrateLegacyStyxV1IfNeeded();
  const db = await getGlobalDb();
  const row = await db.get("meta", META_IDENTITY_KEY);
  if (!row) return undefined;
  if (typeof row.email !== "string") {
    await db.delete("meta", META_IDENTITY_KEY);
    return undefined;
  }
  return row;
}

export async function saveIdentity(row: IdentityRow): Promise<void> {
  const db = await getGlobalDb();
  await db.put("meta", row, META_IDENTITY_KEY);
}

export async function stripIdentitySecret(): Promise<void> {
  const db = await getGlobalDb();
  const row = await loadIdentity();
  if (row) {
    await db.put("meta", { ...row, secretKeyEdB64: "" }, META_IDENTITY_KEY);
  }
}

export async function clearUserScopedData(): Promise<void> {
  if (!activeUserId) return;
  const db = await getDb();
  const tx = db.transaction(["meta", "contacts", "messages", "outbox", "link_preview_cache", "image_atr_cache"], "readwrite");
  await tx.objectStore("meta").clear();
  await tx.objectStore("contacts").clear();
  await tx.objectStore("messages").clear();
  if (tx.objectStoreNames.contains("outbox")) {
    await tx.objectStore("outbox").clear();
  }
  await tx.objectStore("link_preview_cache").clear();
  await tx.objectStore("image_atr_cache").clear();
  await tx.done;
}

export async function clearEncryptedLocalCaches(): Promise<void> {
  if (!activeUserId) return;
  const db = await getDb();
  const tx = db.transaction(["link_preview_cache", "image_atr_cache"], "readwrite");
  await tx.objectStore("link_preview_cache").clear();
  await tx.objectStore("image_atr_cache").clear();
  await tx.done;
}

export async function clearAllData(): Promise<void> {
  await clearUserScopedData();
}
